import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getErrorFromCatch } from "../../api/api";
import { ICouponForm, couponApi } from "../../api/coupon.api";
import { IPageParams } from "../../api/IApi";
import { icons } from "../../icons/IconSet";
import { ICoupon, TCouponSource, TCouponState } from "../../model/ICoupon";
import parseISOToString from "../../utils/parseISOToString";
import priceFormatter from "../../utils/priceFormatter";
import Modal from "../common/Modal";
import LoadingOverlay from "../common/loading/LoadingOverlay";
import Table, { ITableTh } from "../common/table/Table";
import { readPageSize } from "../common/table/tablePageSize";
import Button from "../form/Button";
import Input from "../form/Input";
import CouponForm from "./CouponForm";

const VIEW = 'coupons';

// --
// -- Sort keys are the backend's own property names, which is what its
// -- Pageable resolves. "expiresOn" is translated there to the exp_date column
// -- it comes from; anything it does not recognise is dropped rather than
// -- answered with a 500.
// --
const COLUMNS: ITableTh[] = [
  { title: 'ID', sortCol: 'id' },
  { title: 'Code', sortCol: 'code' },
  { title: 'Name', sortCol: 'name' },
  { title: 'Type', sortCol: 'typeId' },
  { title: 'Discount', sortCol: 'value1' },
  { title: 'Min. Order', sortCol: 'value2' },
  { title: 'Part Category', sortCol: 'partCategoryId' },
  { title: 'Created', sortCol: 'creationDate' },
  { title: 'Expires', sortCol: 'expiresOn' },
  { title: 'Status' },
  { title: 'Source', sortCol: 'autoCreated' },
  { title: 'Actions', align: 'right' },
];

const SOURCE_OPTIONS: { title: string, value: TCouponSource }[] = [
  { title: 'Manual coupons', value: 'MANUAL' },
  { title: 'Automatic coupons', value: 'AUTOMATIC' },
  { title: 'All coupons', value: 'ALL' },
];

const STATE_OPTIONS: { title: string, value: TCouponState }[] = [
  { title: 'Active', value: 'ACTIVE' },
  { title: 'Expired', value: 'EXPIRED' },
  { title: 'Any status', value: 'ALL' },
];

/** yyyy-MM-dd as it reads in the US, without a time zone conversion. */
const formatDay = (day: string | null): string => {
  if (!day) return '';
  const [year, month, date] = day.split('-');
  return year && month && date ? `${Number(month)}/${Number(date)}/${year}` : day;
};

const discountOf = (coupon: ICoupon): string => coupon.percentage
  ? `${coupon.value1}%`
  : priceFormatter(coupon.value1);

/**
 * Coupons and discount codes.
 *
 * <p>Opens on <b>active coupons that someone made by hand</b>, which is the
 * working set: the automatic ones are issued and cleaned up by the
 * repeat-purchase sweep without anybody touching them, and there are far more
 * of them than of these.
 */
const CouponList: FC = () => {
  const [source, setSource] = useState<TCouponSource>('MANUAL');
  const [state, setState] = useState<TCouponState>('ACTIVE');
  const [search, setSearch] = useState<string>('');
  const [query, setQuery] = useState<string>('');

  const [pageParams, setPageParams] = useState<IPageParams>(() => ({
    page: 0,
    size: readPageSize(VIEW, 30),
    q: '',
    sort: 'creationDate',
    sortDir: 'desc',
  }));

  const { data: coupons, isFetching, isError, error } = couponApi.useGetCouponsQuery({
    page: pageParams.page,
    size: pageParams.size,
    sort: pageParams.sort,
    sortDir: pageParams.sortDir,
    source,
    state,
    q: query,
  });

  // Loaded once for the form's type dropdown; the list itself never needs it.
  const { data: types } = couponApi.useGetCouponTypesQuery();

  const [createCoupon, { isLoading: isCreating }] = couponApi.useCreateCouponMutation();
  const [updateCoupon, { isLoading: isUpdating }] = couponApi.useUpdateCouponMutation();
  const [deleteCoupon, { isLoading: isDeleting }] = couponApi.useDeleteCouponMutation();

  const [isFormShown, setIsFormShown] = useState<boolean>(false);
  const [editing, setEditing] = useState<ICoupon | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ICoupon | null>(null);

  const isSaving = isCreating || isUpdating;

  useEffect(() => {
    isError && error && toast.error(`Could not load coupons. ${getErrorFromCatch(error)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // A new filter is a new result set; staying on page 7 of the old one lands
  // on an empty table.
  const firstPage = () => setPageParams((prev) => ({ ...prev, page: 0 }));

  const runSearch = (term: string) => {
    setQuery(term.trim());
    firstPage();
  };

  const openCreate = () => {
    setEditing(null);
    setIsFormShown(true);
  };

  const openEdit = (coupon: ICoupon) => {
    setEditing(coupon);
    setIsFormShown(true);
  };

  const closeForm = () => {
    setIsFormShown(false);
    setEditing(null);
  };

  const handleSubmit = async (form: ICouponForm) => {
    try {
      if (editing) {
        await updateCoupon({ id: editing.id, ...form }).unwrap();
        toast.success('Coupon updated');
      } else {
        await createCoupon(form).unwrap();
        toast.success('Coupon created');
      }
      closeForm();
    } catch (err) {
      toast.error(getErrorFromCatch(err) || 'Failed to save the coupon');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCoupon(deleteTarget.id).unwrap();
      toast.success('Coupon deleted');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getErrorFromCatch(err) || 'Failed to delete the coupon');
    }
  };

  return (
    <div className="relative bg-white rounded-lg shadow-sm flex flex-col flex-1 min-h-0">
      <div className="flex justify-between gap-6 items-end flex-wrap px-4 sm:px-6 lg:px-8 pt-5">
        <div className="flex gap-4 items-end flex-wrap">
          <Input
            label="Source"
            name="source"
            value={source}
            options={SOURCE_OPTIONS}
            handle={(e) => {
              setSource((e as React.ChangeEvent<HTMLSelectElement>).target.value as TCouponSource);
              firstPage();
            }}
            disabled={isFetching}
            classNameField="min-w-[180px]"
          />
          <Input
            label="Status"
            name="state"
            value={state}
            options={STATE_OPTIONS}
            handle={(e) => {
              setState((e as React.ChangeEvent<HTMLSelectElement>).target.value as TCouponState);
              firstPage();
            }}
            disabled={isFetching}
            classNameField="min-w-[150px]"
          />
          {/* --
              -- Run on Enter or on the button, not on every keystroke: the
              -- table is paged on the server, so typing through it would be a
              -- request per character.
              --
              -- Deliberately not a <form>. Button does not pass its `type`
              -- through to the element, so every button inside a form is a
              -- submit button - and Clear would have submitted the search it
              -- had just emptied.
              -- */}
          <div className="flex gap-2 items-end">
            <Input
              label="Search"
              name="q"
              value={search}
              placeholder="Code or name"
              handle={(e) => setSearch((e as React.ChangeEvent<HTMLInputElement>).target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') runSearch(search);
              }}
              disabled={isFetching}
              classNameField="min-w-[200px]"
            />
            <Button
              isNotFullWidth
              size="small"
              variant="outline"
              onClick={() => runSearch(search)}
            >
              Search
            </Button>
            {!!query && (
              <Button
                isNotFullWidth
                size="small"
                variant="outline"
                onClick={() => {
                  setSearch('');
                  runSearch('');
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 whitespace-nowrap">
          <span className="text-text_pale">{coupons?.totalElements ?? 0} coupon(s)</span>
          <Button
            isNotFullWidth
            type="submit"
            size="small"
            className="whitespace-nowrap flex items-center gap-1"
            onClick={openCreate}
          >
            {icons('plus', { width: '16px' })}
            Add Coupon
          </Button>
        </div>
      </div>

      {isFetching && coupons?.content?.length ? <LoadingOverlay /> : null}

      <div className="flex-1 min-h-0 flex flex-col">
        <Table
          fillHeight
          isPending={isFetching}
          thead={COLUMNS}
          pagable={coupons}
          pageParams={pageParams}
          updatePageParams={(next) => setPageParams((prev) => ({ ...prev, ...next }))}
          isResizable
          resizeStorageKey={VIEW}
          pageSizeStorageKey={VIEW}
          tbody={(coupons?.content || []).map((coupon) => ({
            onDoubleClick: () => openEdit(coupon),
            data: [
              coupon.id,
              <span className="font-semibold">{coupon.code}</span>,
              coupon.name,
              // A type the storefront does not handle discounts nothing and
              // says nothing, so a row carrying one is worth pointing at.
              coupon.typeSupported
                ? coupon.typeLabel
                : <span className="text-red-600" title="The storefront does not honour this type">
                    {`Type ${coupon.typeId} (unsupported)`}
                  </span>,
              discountOf(coupon),
              coupon.value2 ? priceFormatter(coupon.value2) : 'Any',
              coupon.partCategoryId ? coupon.partCategoryId : '—',
              coupon.creationDate ? parseISOToString(coupon.creationDate) : '',
              formatDay(coupon.expiresOn),
              coupon.expired
                ? <span className="text-text_pale">Expired</span>
                : <span className="text-green-700">Active</span>,
              coupon.autoCreated ? 'Automatic' : 'Manual',
              <div className="flex gap-2 justify-end">
                <Button
                  isNotFullWidth
                  type="button"
                  size="small"
                  onClick={() => openEdit(coupon)}
                >
                  {icons('edit-record', { width: '16px' })}
                </Button>
                <Button
                  isNotFullWidth
                  type="delete"
                  variant="outline"
                  size="small"
                  onClick={() => setDeleteTarget(coupon)}
                >
                  {icons('x', { width: '16px' })}
                </Button>
              </div>,
            ],
          }))}
        />
      </div>

      {isFormShown && (
        <Modal
          isShown={isFormShown}
          title={editing ? `Edit Coupon ${editing.code}` : 'New Coupon'}
          style={{ maxWidth: '720px', overflowY: 'visible' }}
          onClose={closeForm}
        >
          <CouponForm
            coupon={editing}
            types={types || []}
            loading={isSaving}
            onSubmit={handleSubmit}
          />
        </Modal>
      )}

      {!!deleteTarget && (
        <Modal
          isShown={!!deleteTarget}
          title="Delete Coupon"
          style={{ maxWidth: '520px' }}
          onClose={() => setDeleteTarget(null)}
        >
          <div className="flex flex-col gap-6">
            <div>
              Delete <b>{deleteTarget.code}</b>
              {deleteTarget.name ? ` (${deleteTarget.name})` : ''}?
              {' '}
              The code stops working at checkout straight away.
              {deleteTarget.autoCreated && (
                // Worth saying: this one was issued automatically, and a
                // customer was emailed it 90 days after their purchase.
                <div className="mt-2 text-text_pale">
                  This coupon was issued automatically, so a customer has been
                  emailed the code.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button
                isNotFullWidth
                type="button"
                disabled={isDeleting}
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </Button>
              <Button
                isNotFullWidth
                type="delete"
                variant="outline"
                disabled={isDeleting}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CouponList;
